import {
    DynamicSublocalityFiltersExperiment,
    DynamicSublocalityFiltersExperimentValues,
    useServerEnrolledStatsigExperiment,
} from '@eventbrite/discover-utils';
import { GenericLazyString } from '@eventbrite/i18n';
import { useMemo } from 'react';
import type { SearchResponseAggs } from '../../../../api';
import { getParentLocationForSlug, type Sublocalities } from './sublocalities';

export const useSublocalityFilters = (
    locationSlug?: string,
    aggs?: SearchResponseAggs,
) => {
    const isInDynamicSublocalityExperiment =
        useIsInDynamicSublocalityExperiment();

    const parentLocation = useMemo(
        () => getParentLocationForSlug(locationSlug),
        [locationSlug],
    );

    const sublocalitiesFromAggs = useMemo(
        () => transformSublocalitesAggs(aggs),
        [aggs],
    );

    // If the user is not in the new experiment but is in one of the four metros from the
    // previous experiment, we still want to show them the old neighborhood filters
    if (!isInDynamicSublocalityExperiment && !!parentLocation) {
        return parentLocation.sublocalities;
    } else if (isInDynamicSublocalityExperiment) {
        return sublocalitiesFromAggs;
    }
};

export const useIsInDynamicSublocalityExperiment = () => {
    const SublocalityFiltersExperimentVariant =
        useServerEnrolledStatsigExperiment<DynamicSublocalityFiltersExperimentValues>(
            {
                name: DynamicSublocalityFiltersExperiment.name,
                paramName: DynamicSublocalityFiltersExperiment.paramName,
                defaultValue:
                    DynamicSublocalityFiltersExperiment.values.control,
                enabled: true,
            },
        ).variant;

    return (
        SublocalityFiltersExperimentVariant ===
            DynamicSublocalityFiltersExperimentValues.test_without_ads ||
        SublocalityFiltersExperimentVariant ===
            DynamicSublocalityFiltersExperimentValues.test_with_ads
    );
};

const transformSublocalitesAggs = (
    aggs: SearchResponseAggs = {},
): Sublocalities => {
    const { places_borough, places_neighborhood } = aggs;

    // In the future we may want to separate out boroughs vs neighborhoods as their
    // own filters. For now, they're combined into one filter set, sorted by event count
    const allPlaceAggs = [
        ...(places_borough?.buckets || []),
        ...(places_neighborhood?.buckets || []),
    ].sort((a, b) => b.doc_count - a.doc_count);

    return allPlaceAggs.reduce((acc: Sublocalities, cur) => {
        const locationId = `${cur.inflated.place_type}/${cur.inflated.id}`;
        acc[locationId] = new GenericLazyString(() => cur.display_name);

        return acc;
    }, {});
};
